import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SOURCES, SOURCE_META, DIAMETERS, TUBE_TYPES } from "@/lib/funnel";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function EditLeadDialog({ open, onOpenChange, lead, onSaved }) {
  const [form, setForm] = useState(null);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && lead) {
      setForm({
        company_name: lead.company_name || "",
        contact_name: lead.contact_name || "",
        email: lead.email || "",
        phone: lead.phone || "",
        source: lead.source || "website",
        source_detail: lead.source_detail || "",
        priority: lead.priority || "medium",
        product_requirement: lead.product_requirement || "",
        quantity: lead.quantity || "",
        estimated_value: lead.estimated_value != null ? String(lead.estimated_value) : "",
        diameter: lead.diameter || "none",
        tube_type: lead.tube_type || "none",
        city: lead.city || "",
        specifications: lead.specifications || "",
        assigned_to: lead.assigned_to || "unassigned",
      });
      api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    }
  }, [open, lead]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        company_name: form.company_name,
        contact_name: form.contact_name || null,
        email: form.email || null,
        phone: form.phone || null,
        source: form.source,
        source_detail: form.source === "other" ? (form.source_detail || null) : null,
        priority: form.priority,
        product_requirement: form.product_requirement || null,
        quantity: form.quantity || null,
        estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null,
        diameter: form.diameter === "none" ? null : form.diameter,
        tube_type: form.tube_type === "none" ? null : form.tube_type,
        city: form.city || null,
        specifications: form.specifications || null,
        assigned_to: form.assigned_to === "unassigned" ? null : form.assigned_to,
      };
      await api.patch(`/leads/${lead.id}`, payload);
      toast.success("Lead updated");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit Lead</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2">
            <Label className="text-xs">Company Name *</Label>
            <Input data-testid="edit-company-input" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Contact Name</Label>
            <Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input data-testid="edit-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Email</Label>
            <Input value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Source</Label>
            <Select value={form.source} onValueChange={(v) => set("source", v)}>
              <SelectTrigger data-testid="edit-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => <SelectItem key={s} value={s}>{SOURCE_META[s].label}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.source === "other" && (
              <Input
                className="mt-2"
                data-testid="edit-source-detail-input"
                placeholder="Type the actual source"
                value={form.source_detail}
                onChange={(e) => set("source_detail", e.target.value)}
              />
            )}
          </div>
          <div>
            <Label className="text-xs">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger data-testid="edit-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Product Requirement</Label>
            <Input value={form.product_requirement} onChange={(e) => set("product_requirement", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Quantity</Label>
            <Input value={form.quantity} onChange={(e) => set("quantity", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Est. Value (INR)</Label>
            <Input type="number" value={form.estimated_value} onChange={(e) => set("estimated_value", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Diameter (Dia)</Label>
            <Select value={form.diameter} onValueChange={(v) => set("diameter", v)}>
              <SelectTrigger data-testid="edit-diameter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not specified</SelectItem>
                {DIAMETERS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Tube Type</Label>
            <Select value={form.tube_type} onValueChange={(v) => set("tube_type", v)}>
              <SelectTrigger data-testid="edit-tubetype-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not specified</SelectItem>
                {TUBE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">City / Location</Label>
            <Input data-testid="edit-city-input" value={form.city} onChange={(e) => set("city", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Specifications / Notes</Label>
            <Textarea rows={2} value={form.specifications} onChange={(e) => set("specifications", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Assign To</Label>
            <Select value={form.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
              <SelectTrigger data-testid="edit-assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="save-edit-lead-btn" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
